import { doc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const DAILY_VISIT_KEY_PREFIX = "mfp_daily_rollup_visit_";

export const LOW_COST_MODE = process.env.REACT_APP_LOW_COST_MODE !== "false";

export const IMPORTANT_APP_EVENTS = new Set([
  "signup_completed",
  "stand_create_failed",
  "farm_stand_completed",
  "add_product_completed",
  "job_post_completed",
  "community_post_completed",
  "community_post_create_failed",
]);

export const IMPORTANT_USAGE_EVENTS = new Set([
  "public_session_started",
  "public_page_view",
  "public_home_view",
  "public_marketplace_view",
  "public_product_open",
  "public_farm_stands_view",
  "public_stand_open",
  "public_live_shelf_view",
  "public_live_shelf_item_open",
  "public_events_view",
  "public_event_open",
  "public_jobs_view",
  "public_job_open",
  "public_plants_garden_view",
  "public_land_share_view",
  "public_land_share_open",
  "auth_gate_open",
  "signup_click",
  "signin_click",
  "request_to_buy_intent",
  "message_seller_intent",
  "job_apply_intent",
  "job_bid_intent",
  "event_participation_intent",
  "land_share_contact_or_post_intent",
  "home_explore_local_click",
  "home_marketplace_click",
  "home_farm_stands_click",
  "home_events_click",
  "home_jobs_click",
  "home_land_share_click",
  "home_plants_garden_click",
  "home_almanac_click",
  "home_live_shelf_click",
  "home_join_click",
  "registration_completed",
  "farm_stand_created",
  "farm_stand_updated",
  "pickup_spot_created",
  "pickup_spot_updated",
  "stand_create_failed",
  "product_created",
  "product_updated",
  "request_to_buy_started",
  "request_to_buy_completed",
  "job_post_completed",
]);

const EVENT_COUNTERS = {
  add_product_completed: "productCreates",
  campaign_visit: "visits",
  contact_support_submitted: "supportRequests",
  community_post_completed: "communityPostCreates",
  farm_stand_completed: "standCreates",
  farm_stand_created: "standCreates",
  farm_stand_updated: "standUpdates",
  job_post_completed: "jobPostCreates",
  page_view: "visits",
  pickup_spot_created: "standCreates",
  pickup_spot_updated: "standUpdates",
  product_created: "productCreates",
  product_updated: "productUpdates",
  registration_completed: "signups",
  request_to_buy_completed: "requestToBuyCompletes",
  request_to_buy_started: "requestToBuyStarts",
  report_submitted: "reports",
  route_view: "visits",
  signup_completed: "signups",
  stand_create_failed: "standCreateFailures",
};

const getDayId = () => new Date().toISOString().slice(0, 10);

export const getDailyCounterName = (eventType) => EVENT_COUNTERS[eventType] || "";

export const shouldSkipMinorAnalyticsEvent = (eventType, isAnonymous = false) => {
  if (!LOW_COST_MODE) return false;
  if (eventType === "page_view" || eventType === "route_view" || eventType === "route_time_summary") return true;
  if (IMPORTANT_APP_EVENTS.has(eventType) || IMPORTANT_USAGE_EVENTS.has(eventType)) return false;
  if (isAnonymous) return Math.random() >= 0.05;
  return true;
};

export const trackDailyRollup = async (eventType, { oncePerDay = false } = {}) => {
  if (typeof window === "undefined") return false;

  const counterName = getDailyCounterName(eventType);
  if (!counterName) return false;

  const dayId = getDayId();
  if (oncePerDay) {
    const visitKey = `${DAILY_VISIT_KEY_PREFIX}${dayId}`;
    try {
      if (window.sessionStorage.getItem(visitKey)) return false;
      window.sessionStorage.setItem(visitKey, "true");
    } catch (error) {}
  }

  try {
    await setDoc(
      doc(db, "analyticsDaily", dayId),
      {
        [counterName]: increment(1),
        date: dayId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Daily analytics rollup was not recorded:", error?.code || error?.message);
    }
    return false;
  }
};
